import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCheck, Trash2, X, CalendarCheck, CalendarClock, CalendarX,
  CreditCard, Clock, RefreshCw, BellOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import NotificationPreferences from '../components/NotificationPreferences';

const TYPE_ICON = {
  booking_confirmed: CalendarCheck,
  booking_rescheduled: CalendarClock,
  booking_cancelled: CalendarX,
  new_booking: CalendarCheck,
  payment_received: CreditCard,
  daily_summary: CreditCard,
  reminder: Clock,
  return_reminder: RefreshCw,
};

function fullDate(iso, language) {
  try {
    return new Date(iso).toLocaleString(language === 'pt' ? 'pt-PT' : 'en-GB', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function dayLabel(iso, language) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date();
    yest.setDate(today.getDate() - 1);
    const same = (a, b) => a.toDateString() === b.toDateString();
    if (same(d, today)) return language === 'pt' ? 'Hoje' : 'Today';
    if (same(d, yest)) return language === 'pt' ? 'Ontem' : 'Yesterday';
    return d.toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function NotificationsCenterPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { language } = useLanguage();

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications', { params: { limit: 200 } });
      setNotifications(data.notifications || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const unread = notifications.filter((n) => !n.read).length;

  const handleClick = async (n) => {
    if (!n.read) {
      setNotifications((prev) => prev.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x)));
      try { await api.put(`/notifications/${n.notification_id}/read`); } catch { /* noop */ }
    }
    if (n.url) navigate(n.url);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    setNotifications((prev) => prev.filter((x) => x.notification_id !== id));
    try { await api.delete(`/notifications/${id}`); } catch { /* noop */ }
  };

  const handleMarkAll = async () => {
    setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
    try { await api.put('/notifications/read-all'); } catch { /* noop */ }
  };

  const handleClearAll = async () => {
    setNotifications([]);
    try {
      await api.delete('/notifications');
      toast.success(language === 'pt' ? 'Notificações limpas.' : 'Notifications cleared.');
    } catch { /* noop */ }
  };

  // Agrupar por dia
  const groups = [];
  const map = {};
  notifications.forEach((n) => {
    const key = dayLabel(n.created_at, language);
    if (!map[key]) { map[key] = []; groups.push(key); }
    map[key].push(n);
  });

  return (
    <div className="min-h-screen bg-[#F7F4EF] pt-24 pb-16 px-4" data-testid="notifications-center-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-full bg-[#BFA05F]/15 flex items-center justify-center">
            <Bell className="w-5 h-5 text-[#BFA05F]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-[#1A1815]">
              {language === 'pt' ? 'Central de Notificações' : 'Notification Center'}
            </h1>
            <p className="text-sm text-[#635E54]">
              {unread > 0
                ? (language === 'pt' ? `${unread} por ler` : `${unread} unread`)
                : (language === 'pt' ? 'Tudo em dia' : 'All caught up')}
            </p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          {/* Lista de notificações */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden order-2 lg:order-1">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E2DA]">
              <span className="font-heading text-base font-semibold text-[#1A1815]">
                {language === 'pt' ? 'As suas notificações' : 'Your notifications'}
              </span>
              <div className="flex items-center gap-4">
                {unread > 0 && (
                  <button
                    onClick={handleMarkAll}
                    className="text-xs text-[#BFA05F] hover:underline flex items-center gap-1"
                    data-testid="center-mark-all-btn"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    {language === 'pt' ? 'Marcar todas' : 'Mark all'}
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="text-xs text-[#B0483B] hover:underline flex items-center gap-1"
                    data-testid="center-clear-all-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {language === 'pt' ? 'Limpar tudo' : 'Clear all'}
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="px-5 py-16 text-center text-sm text-[#A8A29A]">
                {language === 'pt' ? 'A carregar...' : 'Loading...'}
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-5 py-20 text-center" data-testid="center-empty">
                <BellOff className="w-10 h-10 text-[#D8D2C6] mx-auto mb-3" />
                <p className="text-sm text-[#A8A29A]">
                  {language === 'pt' ? 'Não tem notificações.' : 'You have no notifications.'}
                </p>
              </div>
            ) : (
              groups.map((g) => (
                <div key={g}>
                  <div className="px-5 py-2 bg-[#FBF9F4] text-[11px] font-semibold uppercase tracking-wide text-[#A8A29A]">
                    {g}
                  </div>
                  {map[g].map((n) => {
                    const Icon = TYPE_ICON[n.type] || Bell;
                    return (
                      <div
                        key={n.notification_id}
                        onClick={() => handleClick(n)}
                        data-testid={`center-item-${n.notification_id}`}
                        className={`group flex gap-3 px-5 py-4 border-b border-[#F1EEE8] cursor-pointer hover:bg-[#FBF9F4] transition-colors ${
                          n.read ? '' : 'bg-[#FBF9F4]'
                        }`}
                      >
                        <span className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${n.read ? 'bg-[#F1EEE8]' : 'bg-[#BFA05F]/15'}`}>
                          <Icon className={`w-4 h-4 ${n.read ? 'text-[#A8A29A]' : 'text-[#BFA05F]'}`} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#1A1815]">{n.title}</span>
                            {!n.read && <span className="w-2 h-2 rounded-full bg-[#BFA05F] flex-shrink-0" />}
                          </div>
                          <p className="text-sm text-[#635E54] mt-0.5">{n.body}</p>
                          <span className="text-[11px] text-[#A8A29A] mt-1 block">{fullDate(n.created_at, language)}</span>
                        </div>
                        <button
                          onClick={(e) => handleDelete(e, n.notification_id)}
                          data-testid={`center-delete-${n.notification_id}`}
                          className="flex-shrink-0 p-1.5 rounded-full text-[#C4BEB2] hover:text-[#B0483B] hover:bg-[#F1EEE8] transition-colors md:opacity-0 md:group-hover:opacity-100"
                          aria-label="Apagar"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Preferências */}
          <div className="order-1 lg:order-2">
            <NotificationPreferences />
          </div>
        </div>
      </div>
    </div>
  );
}
